import { yupResolver } from '@hookform/resolvers/yup';
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import * as yup from 'yup';
import { ContactFormData, contactService } from '../services/contactService';

// Form validation schema
const contactSchema = yup.object({
  name: yup.string().required('お名前は必須です'),
  company: yup.string().required('会社名・団体名は必須です'),
  phone: yup.string().required('電話番号は必須です').matches(
      /^0[789]0-?\d{4}-?\d{4}$/,
      '有効な電話番号を入力してください (例: 080-1234-5678)'
    ),
  email: yup.string().email('有効なメールアドレスを入力してください').required('メールアドレスは必須です'),
  emailConfirm: yup.string()
    .oneOf([yup.ref('email')], 'メールアドレスが一致しません')
    .required('メールアドレス(確認)は必須です'),
  message: yup.string().required('お問い合わせ内容は必須です'),
  privacyPolicy: yup.boolean().required('プライバシーポリシーに同意する必要があります').oneOf([true], 'プライバシーポリシーに同意する必要があります'),
}).required();

// ContactFormData is now imported from contactService

type ContactState = 'input' | 'confirmation' | 'result';

interface ContactPageProps {
  formData?: ContactFormData;
  onBackToInput?: () => void;
}

const ContactPage: React.FC<ContactPageProps> = ({ formData, onBackToInput }) => {
  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
    }, []);

  const [currentState, setCurrentState] = useState<ContactState>('input');
  const [submittedData, setSubmittedData] = useState<ContactFormData | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitResult, setSubmitResult] = useState<{ success: boolean; message: string } | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
    watch,
    reset
  } = useForm<ContactFormData>({
    resolver: yupResolver(contactSchema),
    mode: 'onChange'
  });

  const watchedValues = watch();

  const onSubmit = (data: ContactFormData) => {
    setSubmittedData(data);
    setCurrentState('confirmation');
    window.scrollTo(0, 0);
  };

  const handleConfirm = async () => {
    if (!submittedData) return;
    
    setIsSubmitting(true);
    try {
      const result = await contactService.submitContactForm(submittedData);
      
      setSubmitResult({
        success: result.success,
        message: result.message
      });
    } catch (error) {
      setSubmitResult({
        success: false,
        message: '送信に失敗しました。しばらく時間をおいて再度お試しください。'
      });
    } finally {
      setIsSubmitting(false);
      setCurrentState('result');
      window.scrollTo(0, 0);
    }
  };

  const handleBackToInput = () => {
    setCurrentState('input');
    setSubmittedData(null);
    setSubmitResult(null);
    window.scrollTo(0, 0);
  };

  const handleBackToTop = () => {
    window.location.href = '/';
  };

  // Input Form State
  if (currentState === 'input') {
    return (
      <ContactContainer $state="input">

      {/* Breadcrumb Navigation */}
      <BreadcrumbSection>
        <BreadcrumbContainer>
          <BreadcrumbText>
            <Link to="/">トップ</Link> ー お問い合わせ
          </BreadcrumbText>
        </BreadcrumbContainer>
      </BreadcrumbSection>

      {/* Page Title Section */}
      <PageTitleSection>
          <PageTitleMain>お問い合わせ</PageTitleMain>
          <PageTitleSub>Contact</PageTitleSub>
      </PageTitleSection>
        
        <FormContainer>
          <FormCard>
            <FormDescription>
              3営業日を過ぎても返信がない場合は、お手数ですが再度お問い合わせをお願いいたします。
            </FormDescription>
            
            <Form onSubmit={handleSubmit(onSubmit)}>
              <FormField>
                <Label>
                  お名前<RequiredMark>*</RequiredMark>
                </Label>
                <InputFieldContainer>
                  <Input
                    type="text"
                    {...register('name')}
                    placeholder="お名前を入力してください"
                    hasError={!!errors.name}
                  />
                  {errors.name && <ErrorMessage>{errors.name.message}</ErrorMessage>}
                </InputFieldContainer>
              </FormField>

              <FormField>
                <Label>
                  会社名・団体名<RequiredMark>*</RequiredMark>
                </Label>
                <InputFieldContainer>
                  <Input
                    type="text"
                    {...register('company')}
                    placeholder="会社名・団体名を入力してください"
                    hasError={!!errors.company}
                  />
                  {errors.company && <ErrorMessage>{errors.company.message}</ErrorMessage>}
                </InputFieldContainer>
              </FormField>

              <FormField>
                <Label>
                  電話番号<RequiredMark>*</RequiredMark>
                </Label>
                <InputFieldContainer>
                  <Input
                    type="tel"
                    {...register('phone')}
                    placeholder="電話番号を入力してください"
                    hasError={!!errors.phone}
                  />
                  {errors.phone && <ErrorMessage>{errors.phone.message}</ErrorMessage>}
                </InputFieldContainer>
              </FormField>

              <FormField>
                <Label>
                  メールアドレス<RequiredMark>*</RequiredMark>
                </Label>
                <InputFieldContainer>
                  <Input
                    type="email"
                    {...register('email')}
                    placeholder="メールアドレスを入力してください"
                    hasError={!!errors.email}
                  />
                  {errors.email && <ErrorMessage>{errors.email.message}</ErrorMessage>}
                </InputFieldContainer>
              </FormField>

              <FormField>
                <Label>
                  メールアドレス(確認)<RequiredMark>*</RequiredMark>
                </Label>
                <InputFieldContainer>
                  <Input
                    type="email"
                    {...register('emailConfirm')}
                    placeholder="メールアドレスを再入力してください"
                    hasError={!!errors.emailConfirm}
                  />
                  {errors.emailConfirm && <ErrorMessage>{errors.emailConfirm.message}</ErrorMessage>}
                </InputFieldContainer>
              </FormField>

              <FormField>
                <Label>
                  お問い合わせ内容<RequiredMark>*</RequiredMark>
                </Label>
                <InputFieldContainer>
                  <Textarea
                    {...register('message')}
                    placeholder="お問い合わせ内容を入力してください"
                    hasError={!!errors.message}
                    rows={6}
                  />
                  {errors.message && <ErrorMessage>{errors.message.message}</ErrorMessage>}
                </InputFieldContainer>
              </FormField>

              <FormField>
                <InputFieldContainer>
                  <CheckboxContainer>
                    <Checkbox>
                      <CheckboxMark
                        type="checkbox"
                        {...register('privacyPolicy')}
                        id="privacyPolicy"
                      />
                      <CheckboxLabel htmlFor="privacyPolicy">
                        プライバシーポリシーに同意する
                      </CheckboxLabel>
                      
                    </Checkbox>
                    {errors.privacyPolicy && <ErrorMessage>{errors.privacyPolicy.message}</ErrorMessage>}
                  </CheckboxContainer>
                  <PrivacypolicyLink href="/privacy-policy/" target="_blank">プライバシーポリシーはこちら</PrivacypolicyLink>
                </InputFieldContainer>
              </FormField>

              <ButtonContainer>
                <SubmitButtonConfirm type="submit" disabled={!isValid}>
                  入力内容を確認する
                </SubmitButtonConfirm>
              </ButtonContainer>
            </Form>
          </FormCard>
        </FormContainer>
      </ContactContainer>
    );
  }

  // Confirmation State
  if (currentState === 'confirmation' && submittedData) {
    return (
      <ContactContainer $state="confirmation">

        {/* Breadcrumb Navigation */}
        <BreadcrumbSection>
          <BreadcrumbContainer>
            <BreadcrumbText>
              <Link to="/">トップ</Link> ー お問い合わせ
            </BreadcrumbText>
          </BreadcrumbContainer>
        </BreadcrumbSection>

        {/* Page Title Section */}
        <PageTitleSection>
            <PageTitleMain>お問い合わせ</PageTitleMain>
            <PageTitleSub>Contact</PageTitleSub>
        </PageTitleSection>

        <FormContainer>
          <FormCard>
              <ConfirmationSection>
                <FormDescription>
                下記内容の確認の上、送信ボタンを押してください
              </FormDescription>
              <ConfirmationField>
                <ConfirmationLabel>お名前</ConfirmationLabel>
                <ConfirmationValue>{submittedData.name}</ConfirmationValue>
              </ConfirmationField>

              <ConfirmationField>
                <ConfirmationLabel>会社名・団体名</ConfirmationLabel>
                <ConfirmationValue>{submittedData.company}</ConfirmationValue>
              </ConfirmationField>

              <ConfirmationField>
                <ConfirmationLabel>電話番号</ConfirmationLabel>
                <ConfirmationValue>{submittedData.phone}</ConfirmationValue>
              </ConfirmationField>

              <ConfirmationField>
                <ConfirmationLabel>メールアドレス</ConfirmationLabel>
                <ConfirmationValue>{submittedData.email}</ConfirmationValue>
              </ConfirmationField>

              <ConfirmationField>
                <ConfirmationLabel>メールアドレス(確認)</ConfirmationLabel>
                <ConfirmationValue>{submittedData.emailConfirm}</ConfirmationValue>
              </ConfirmationField>

              <ConfirmationField>
                <ConfirmationLabel>お問い合わせ内容</ConfirmationLabel>
                <ConfirmationValue>{submittedData.message}</ConfirmationValue>
              </ConfirmationField>

            </ConfirmationSection>

            <ButtonContainer>
              <BackButton type="button" onClick={handleBackToInput}>
                入力内容を変更
              </BackButton>
              <SubmitButton 
                type="button" 
                onClick={handleConfirm}
                disabled={isSubmitting}
              >
                {isSubmitting ? '送信中...' : '送信する'}
              </SubmitButton>
            </ButtonContainer>
          </FormCard>
        </FormContainer>
      </ContactContainer>
    );
  }

  // Result State
  if (currentState === 'result') {
    return (
      <ContactContainer $state="result">

      {/* Breadcrumb Navigation */}
      <BreadcrumbSection>
        <BreadcrumbContainer>
          <BreadcrumbText>
            <Link to="/">トップ</Link> ー お問い合わせ
          </BreadcrumbText>
        </BreadcrumbContainer>
      </BreadcrumbSection>

      {/* Page Title Section */}
      <PageTitleSection>
          <PageTitleMain>お問い合わせ</PageTitleMain>
          <PageTitleSub>Contact</PageTitleSub>
      </PageTitleSection>

        <FormContainer>
          <FormCard>
            <ResultContainer>
              {submitResult?.success ? (
                <>
                  <ResultMessage>
                    お問い合わせ受付完了メールをご確認ください。お問い合わせ受付完了の自動確認メールを送信しております。
                    <br /><br />
                    暫く経過しても受信しない場合は、ネットワーク上に問題が生じているか、ご入力いただいたメールアドレスに誤りがある可能性があります。再度ご確認ください。
                  </ResultMessage>
                </>
              ) : (
                <>
                  <ResultMessage>
                    {submitResult?.message || '送信に失敗しました。しばらく時間をおいて再度お試しください。'}
                  </ResultMessage>
                </>
              )}
              
              <ButtonContainer>
                <BackButton type="button" onClick={handleBackToTop}>
                  トップに戻る
                </BackButton>
              </ButtonContainer>
            </ResultContainer>
          </FormCard>
        </FormContainer>
      </ContactContainer>
    );
  }

  return null;
};

// Input State Styles
const ContactContainer = styled.div.attrs({className: 'page-container'})<{ $state?: 'input' | 'confirmation' | 'result' }>`
  background: #F9FAFA;
  padding: 0 0 ${props => {
    switch (props.$state) {
      case 'input':
        return '80px';
      case 'confirmation':
        return '63px';
      case 'result':
        return '140px';
      default:
        return '80px';
    }
  }};

  @media (max-width: 768px) {
      padding: 0 20px 120px;
`;

const BreadcrumbSection = styled.div.attrs({className: 'breadcrumb-section'})`
  padding: 80px 0 40px;
`;

const BreadcrumbContainer = styled.div.attrs({className: 'breadcrumb-container'})`
  margin: 0 116px;
  @media (max-width: 768px) {
      margin: 0;
    }
`;

const BreadcrumbText = styled.p.attrs({className: 'breadcrumb-text'})`
  font-family: 'Noto Sans JP', sans-serif;
  font-weight: 500;
  font-size: 16px;
  line-height: 1;
  color: #9B9B9B;
  margin: 0;

  a {
    color: #9B9B9B;
    text-decoration: none;
    transition: color 0.3s ease;

    &:hover {
      color: #0F3F8E;
    }
  }
`;

const PageTitleSection = styled.section.attrs({className: 'page-title-section'})`
  margin: 0 116px;
  @media (max-width: 768px) {
      margin: 0;
    }
`;

const PageTitleMain = styled.h1.attrs({className: 'page-title'})`
  font-family: 'A P-OTF Aoto Gothic StdN', 'Noto Sans JP', sans-serif;
  font-weight: 700;
  font-size: 56px;
  line-height: 1;
  letter-spacing: 0.05em;
  color: #333333;
  margin: 0 0 16px 0;

  @media (max-width: 768px) {
    font-size: 44px;
    line-height: 1.5;
    margin: 0 0 12px 0;
  }
`;

const PageTitleSub = styled.h2.attrs({className: 'page-subtitle'})`
  font-family: 'ITC Avant Garde Gothic Pro', 'Noto Sans JP', sans-serif;
  font-weight: 500;
  font-size: 18px;
  line-height: 1;
  letter-spacing: 0.05em;
  color: #9B9B9B;
  margin: 0;

  @media (max-width: 768px) {
    font-size: 16px;
  }
`;

const FormContainer = styled.div.attrs({className: 'form-container'})`
  margin: 0 116px;
  padding-top: 80px;
  @media (max-width: 768px) {
    margin: 0;
  }
`;

const FormCard = styled.div.attrs({className: 'form-card'})`
  background: #FFFFFF;
  border-radius: 20px;
  padding: 98px 150px 82px;

  @media (max-width: 768px) {
    padding: 55px 35px 80px;
  }
`;

const FormDescription = styled.p.attrs({className: 'form-description'})`
  font-family: 'Noto Sans JP', sans-serif;
  font-weight: 500;
  font-size: 16px;
  color: #333333;
  margin-bottom: 60px;
  line-height: 1.75;

  @media (max-width: 768px) {
    margin-bottom: 41px;
  }
`;

const Form = styled.form.attrs({className: 'form'})`
  max-width: 1280px;
  margin: 0 auto;
`;

const FormField = styled.div.attrs({className: 'form-field'})`
  display: flex;
  align-items: flex-start;
  gap: 40px;
  margin-bottom: 30px;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 10px;
  }
`;

const Label = styled.label.attrs({className: 'label'})`
  display: flex;
  align-items: center;
  font-family: 'Noto Sans JP', sans-serif;
  font-weight: 700;
  font-size: 16px;
  color: #000000;
  min-width: 200px;
  flex-shrink: 0;
  letter-spacing: 0.05em;

  @media (max-width: 768px) {
    min-width: auto;
    margin-bottom: 5px;
  }
`;

const RequiredMark = styled.span.attrs({className: 'required-mark'})`
  color: #FF9294;
  margin-left: 5px;
`;

const InputFieldContainer = styled.div.attrs({className: 'input-field-container'})`
  flex: 1;
  min-width: 0;

  @media (max-width: 768px) {
    width: 100%;

  }
`;

const Input = styled.input.attrs({className: 'input'})<{ hasError: boolean }>`
  width: 100%;
  padding: 15px;
  border: 1px solid ${props => props.hasError ? '#FF9294' : '#E5E5E5'};
  border-radius: 8px;
  font-family: 'Noto Sans JP', sans-serif;
  font-size: 16px;
  background: #FFFFFF;
  transition: border-color 0.3s ease;

  &:focus {
    outline: none;
    border-color: #0F3F8E;
  }

  &::placeholder {
    color: #9B9B9B;
  }
`;

const Textarea = styled.textarea.attrs({className: 'text-area'})<{ hasError: boolean }>`
  height: 280px;
  width: 100%;
  padding: 15px;
  border: 1px solid ${props => props.hasError ? '#FF9294' : '#E5E5E5'};
  border-radius: 8px;
  font-family: 'Noto Sans JP', sans-serif;
  font-size: 16px;
  background: #FFFFFF;
  resize: vertical;
  min-height: 120px;
  transition: border-color 0.3s ease;

  &:focus {
    outline: none;
    border-color: #0F3F8E;
  }

  &::placeholder {
    color: #9B9B9B;
  }
`;

const ErrorMessage = styled.div.attrs({className: 'error-message'})`
  color: #FF9294;
  font-size: 14px;
  margin-top: 5px;
  font-family: 'Noto Sans JP', sans-serif;
`;

const CheckboxContainer = styled.div.attrs({className: 'checkbox-container'})`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
`;

const Checkbox = styled.div.attrs({className: 'checkbox'})`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
`;

const CheckboxMark = styled.input.attrs({className: 'checkbox-mark'})`
  width: 20px;
  height: 20px;
  border: 1px solid #333333;
  border-radius: 4px;
  background: #FFFFFF;
  cursor: pointer;
  accent-color: #0F3F8E;

  &:checked {
    background: #0F3F8E;
    border-color: #0F3F8E;
  }

  &:focus {
    outline: none;
    border-color: #0F3F8E;
  }
`;

const CheckboxLabel = styled.label.attrs({className: 'checkbox-label'})`
  font-family: 'Noto Sans JP', sans-serif;
  font-weight: 500;
  font-size: 16px;
  line-height: 1.75;
  color: #333333;
  cursor: pointer;
  user-select: none;
`;

const ButtonContainer = styled.div.attrs({className: 'button-container'})`
  display: flex;
  justify-content: center;
  gap: 20px;
  margin-top: 60px;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const SubmitButton = styled.button.attrs({className: 'submit-button'})`
  background: #0F3F8E;
  color: #FFFFFF;
  border: 2px solid #0F3F8E;
  border-radius: 26px;
  padding: 12px 40px;
  font-family: 'Noto Sans JP', sans-serif;
  font-weight: 700;
  font-size: 16px;
  cursor: pointer;
  transition: background-color 0.3s ease;
  min-width: 180px;

  &:hover:not(:disabled) {
    background: #0A2B6B;
    color:  #FFFFFF;
  }

  &:disabled {
    background: #f2f2f2;
    border-color: #ccc;
    color: #aaa;
    cursor: not-allowed;
    opacity: 0.7;

  }
`;

const SubmitButtonConfirm = styled(SubmitButton).attrs({className: 'submit-button-confirm'})`
  color: #0F3F8E;
  background: #FFFFFF;
  }
`;

const BackButton = styled.button.attrs({className: 'back-button'})`
  color: #0F3F8E;
  background: #FFFFFF;
  border: 2px solid #0F3F8E;
  border-radius: 26px;
  padding: 12px 40px;
  font-family: 'Noto Sans JP', sans-serif;
  font-weight: 700;
  font-size: 16px;
  cursor: pointer;
  transition: background-color 0.3s ease;
  min-width: 180px;

  &:hover:not(:disabled) {
    background: #0A2B6B;
    color:  #FFFFFF;
  }

  &:disabled {
    background: #f2f2f2;
    border-color: #ccc;
    color: #aaa;
    cursor: not-allowed;
    opacity: 0.7;

  }
`;

// Confirmation State Styles
const ConfirmationSection = styled.div.attrs({className: 'confirmation-section'})`
  max-width: 800px;
  margin: 0 auto;
`;

const ConfirmationField = styled.div.attrs({className: 'confirmation-field'})`
  display: flex;
  align-items: flex-start;
  gap: 40px;
  margin-bottom: 30px;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 10px;
  }
`;

const ConfirmationLabel = styled.div.attrs({className: 'confirmation-label'})`
  display: flex;
  align-items: center;
  font-family: 'Noto Sans JP', sans-serif;
  font-weight: 700;
  font-size: 16px;
  color: #000000;
  min-width: 200px;
  flex-shrink: 0;
  letter-spacing: 0.05em;

  @media (max-width: 768px) {
    min-width: auto;
    margin-bottom: 5px;
  }
`;

const ConfirmationValue = styled.div.attrs({className: 'confirmation-value'})`
  font-family: 'Noto Sans JP', sans-serif;
  font-weight: 500;
  font-size: 16px;
  color: #333333;
  line-height: 1.75;
  word-break: break-word;
`;

// Result State Styles
const ResultContainer = styled.div.attrs({className: 'result-container'})`
  text-align: center;
`;

const ResultMessage = styled.p.attrs({className: 'result-message'})`
  font-family: 'Noto Sans JP', sans-serif;
  font-weight: 700;
  font-size: 16px;
  color: #333333;
  line-height: 1.75;
  margin: 0 0 40px 0;
  text-align: center;
`;


const PrivacypolicyLink =styled.a.attrs({className: 'result-message'})`
  margin: 1em auto;
  display:block;
  width:fit-content;
`;

export default ContactPage;
